import PropTypes from 'prop-types'
import { Arrow } from '../Icons'
import { ChevronDownIcon } from '@heroicons/react/24/solid'

// Link-based sidebar for the Member Area. Unlike the article JumpMenu (which
// renders <button>s that scroll to in-page anchors), these items are cross-page
// links built from the container's `sidebarUrls` API data. The active item is
// driven by `item.isActive` (server-side current-page match), not scroll-spy.

const MemberSidebarItem = ({ item, variant }) => {
  const isActive = !!item.isActive

  if (variant === 'desktop') {
    return (
      <a
        href={item.url}
        aria-current={isActive ? 'page' : undefined}
        className={`jump-menu-item group block text-left w-full font-sans text-lg leading-7 transition-colors ${
          isActive ? 'text-nds-dark-blue font-medium' : 'text-nds-90-black font-normal hover:bg-grey-subtle'
        }`}
      >
        <span
          className={`flex items-center px-6 py-3.5 border-l-[5px] transition-all duration-300 ${
            isActive ? 'border-l-nds-dark-blue' : 'border-l-transparent'
          }`}
        >
          <span className="jump-menu-item__label">{item.label}</span>
        </span>
      </a>
    )
  }

  return (
    <a
      href={item.url}
      aria-current={isActive ? 'page' : undefined}
      className={`jump-menu-item group block text-left w-full text-lg border-t border-primary-border transition-colors font-medium ${
        isActive ? 'bg-grey-subtle text-secondary' : 'text-primary hover:bg-grey-subtle'
      }`}
    >
      <span
        className={`flex gap-2 items-center justify-between px-6 py-3.5 border-l-4 transition-all duration-300 ${isActive ? 'border-l-secondary' : 'border-l-transparent'}`}
      >
        <span className="jump-menu-item__label">{item.label}</span>
        <span className={`shrink-0 ${isActive ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}>
          <Arrow />
        </span>
      </span>
    </a>
  )
}

const MemberSidebarNav = ({ navigationItems, variant, onItemClick }) => {
  return (
    <nav>
      {navigationItems.map((item) => (
        <span key={item.url} onClick={() => onItemClick?.(item)} className="contents">
          <MemberSidebarItem item={item} variant={variant} />
        </span>
      ))}
    </nav>
  )
}

const handleDeviceNavToggle = () => {
  const trigger = document.getElementById('member-sidebar-device-nav-trigger')
  const nav = document.getElementById('member-sidebar-device-nav')
  if (!trigger || !nav) return
  if (trigger.getAttribute('aria-expanded') === 'false') {
    trigger.setAttribute('aria-expanded', 'true')
    nav.setAttribute('aria-hidden', 'false')
  } else {
    trigger.setAttribute('aria-expanded', 'false')
    nav.setAttribute('aria-hidden', 'true')
  }
}

const MemberSidebarMenuDevice = ({ navigationItems, sidebarTitle, onItemClick }) => {
  if (!navigationItems.length) return null
  return (
    <div className="col-span-1 w-full">
      <div className="max-h-[calc(100vh-8rem)] overflow-y-auto relative w-screen left-1/2 -ml-[50vw]">
        <button
          id="member-sidebar-device-nav-trigger"
          aria-controls="member-sidebar-device-nav"
          aria-expanded="false"
          className="flex items-center justify-between w-full text-left bg-nds-blue text-white rounded-none shadow-sm px-4 py-3 transition-colors group/jumpTrigger"
          onClick={handleDeviceNavToggle}
        >
          <span className="font-sans text-base font-normal leading-6 text-white">{sidebarTitle}</span>
          <ChevronDownIcon className="w-6 h-6 text-white group-aria-expanded/jumpTrigger:rotate-180 transition-transform duration-300" />
        </button>
      </div>
      <div
        id="member-sidebar-device-nav"
        aria-hidden="true"
        className="aria-hidden:max-h-0 overflow-hidden max-h-[calc(100vh-8rem)] transition-all duration-300"
      >
        <div className="py-2 px-3">
          <MemberSidebarNav navigationItems={navigationItems} variant="desktop" onItemClick={onItemClick} />
        </div>
      </div>
    </div>
  )
}

const MemberSidebarMenu = ({ navigationItems, sidebarTitle, onItemClick }) => {
  if (!navigationItems.length) return null
  return (
    <div className="lg:col-span-1">
      <div className="sticky top-24 -scale-x-100 max-h-[calc(100vh-8rem)] overflow-y-auto">
        <div className="-scale-x-100 pl-2">
          <h3 className="font-sans text-4xl font-light leading-10 mb-4 text-nds-blue">{sidebarTitle}</h3>
          <MemberSidebarNav navigationItems={navigationItems} variant="desktop" onItemClick={onItemClick} />
        </div>
      </div>
    </div>
  )
}

const itemShape = PropTypes.shape({
  url: PropTypes.string,
  label: PropTypes.string,
  isActive: PropTypes.bool,
})

MemberSidebarItem.propTypes = {
  item: itemShape.isRequired,
  variant: PropTypes.oneOf(['desktop']),
}

MemberSidebarNav.propTypes = {
  navigationItems: PropTypes.arrayOf(itemShape).isRequired,
  variant: PropTypes.oneOf(['desktop']),
  onItemClick: PropTypes.func,
}

MemberSidebarMenuDevice.propTypes = {
  navigationItems: PropTypes.arrayOf(itemShape).isRequired,
  sidebarTitle: PropTypes.string,
  onItemClick: PropTypes.func,
}

MemberSidebarMenu.propTypes = {
  navigationItems: PropTypes.arrayOf(itemShape).isRequired,
  sidebarTitle: PropTypes.string,
  onItemClick: PropTypes.func,
}

export { MemberSidebarMenu, MemberSidebarMenuDevice }
